/**
 * PostgreSQL API Hooks with Firestore-compatible interface for Tenants
 *
 * These hooks wrap the API hooks and transform responses to match
 * the existing Firestore snake_case interface.
 *
 * Usage: Simply change imports from '@/hooks/useTenant' to '@/hooks/api/useTenantCompat'
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '@/lib/api-client';
import type {
  Tenant as FirestoreTenant,
  TenantDoc,
  PricingConfig,
  WidgetConfig,
  BrandingConfig,
  SherwinWilliamsColor,
} from '@/types/firestore';
import type { Tenant as ApiTenant } from './useTenantApi';
import { SHERWIN_WILLIAMS_COLORS } from '@/data/sherwin-williams-colors';

// ============================================
// Transform Functions (camelCase → snake_case)
// ============================================

function transformApiTenantToFirestore(tenant: ApiTenant): FirestoreTenant {
  return {
    id: tenant.id,
    company_name: tenant.companyName,
    slug: tenant.slug,
    pricing_config: tenant.pricingConfig,
    branding_config: tenant.brandingConfig,
    widget_config: tenant.widgetConfig,
    color_config: tenant.colorConfig || undefined,
    custom_colors: tenant.customColors || undefined,
    stripe_customer_id: tenant.stripeCustomerId || undefined,
    subscription_status: tenant.subscriptionStatus,
    subscription_tier: tenant.subscriptionTier,
    trial_ends_at: tenant.trialEndsAt
      ? ({ toDate: () => new Date(tenant.trialEndsAt!) } as any)
      : undefined,
    generations_this_month: tenant.generationsThisMonth,
    // Credit-based subscription
    generation_credits_monthly: tenant.generationCreditsMonthly,
    generation_credits_addon: tenant.generationCreditsAddon,
    generation_credits_rollover: tenant.generationCreditsRollover,
    credits_cap: tenant.creditsCap,
    last_credit_reset_at: tenant.lastCreditResetAt
      ? ({ toDate: () => new Date(tenant.lastCreditResetAt!) } as any)
      : undefined,
    zero_credits_notified: tenant.zeroCreditsNotified,
    created_at: { toDate: () => new Date(tenant.createdAt) } as any,
    // Integration settings
    email_notifications_enabled: tenant.emailNotificationsEnabled,
    webhook_url: tenant.webhookUrl || undefined,
    webhook_enabled: tenant.webhookEnabled,
  };
}

// ============================================
// Compatible Hooks
// ============================================

/**
 * Fetch a single tenant by ID (PostgreSQL API with Firestore-compatible response)
 */
export function useTenant(tenantId: string | undefined) {
  const api = useApiClient();

  return useQuery({
    queryKey: ['tenant', tenantId],
    queryFn: async (): Promise<FirestoreTenant | null> => {
      if (!tenantId) return null;

      try {
        const tenant = await api.get<ApiTenant>(`/tenants/${tenantId}`);
        return transformApiTenantToFirestore(tenant);
      } catch (error) {
        console.error('Failed to fetch tenant:', error);
        return null;
      }
    },
    enabled: !!tenantId,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Update tenant data
 */
export function useUpdateTenant() {
  const queryClient = useQueryClient();
  const api = useApiClient();

  return useMutation({
    mutationFn: async ({
      tenantId,
      data,
    }: {
      tenantId: string;
      data: Partial<Omit<TenantDoc, 'created_at'>>;
    }) => {
      // Transform snake_case to camelCase for API
      const apiData: Record<string, unknown> = {};
      if (data.company_name !== undefined) apiData.companyName = data.company_name;
      if (data.slug !== undefined) apiData.slug = data.slug;
      if (data.pricing_config !== undefined) apiData.pricingConfig = data.pricing_config;
      if (data.branding_config !== undefined) apiData.brandingConfig = data.branding_config;
      if (data.widget_config !== undefined) apiData.widgetConfig = data.widget_config;
      if (data.color_config !== undefined) apiData.colorConfig = data.color_config;
      if (data.custom_colors !== undefined) apiData.customColors = data.custom_colors;

      await api.patch(`/tenants/${tenantId}`, apiData);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['tenant', variables.tenantId] });
    },
  });
}

/**
 * Update tenant pricing config
 */
export function useUpdatePricingConfig() {
  const queryClient = useQueryClient();
  const api = useApiClient();

  return useMutation({
    mutationFn: async ({
      tenantId,
      pricing_config,
    }: {
      tenantId: string;
      pricing_config: PricingConfig;
    }) => {
      await api.patch(`/tenants/${tenantId}`, { pricingConfig: pricing_config });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['tenant', variables.tenantId] });
    },
  });
}

/**
 * Update tenant branding config
 */
export function useUpdateBrandingConfig() {
  const queryClient = useQueryClient();
  const api = useApiClient();

  return useMutation({
    mutationFn: async ({
      tenantId,
      branding_config,
    }: {
      tenantId: string;
      branding_config: BrandingConfig;
    }) => {
      await api.patch(`/tenants/${tenantId}`, { brandingConfig: branding_config });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['tenant', variables.tenantId] });
    },
  });
}

/**
 * Update tenant widget config
 */
export function useUpdateWidgetConfig() {
  const queryClient = useQueryClient();
  const api = useApiClient();

  return useMutation({
    mutationFn: async ({
      tenantId,
      widget_config,
    }: {
      tenantId: string;
      widget_config: WidgetConfig;
    }) => {
      await api.patch(`/tenants/${tenantId}`, { widgetConfig: widget_config });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['tenant', variables.tenantId] });
    },
  });
}

/**
 * Upload logo (stub - needs R2 integration)
 */
export function useUploadLogo() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      tenantId,
      file,
    }: {
      tenantId: string;
      file: File;
    }): Promise<string> => {
      // TODO: Implement R2 upload
      console.warn('Logo upload not yet implemented for PostgreSQL API');
      throw new Error('Logo upload not implemented');
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['tenant', variables.tenantId] });
    },
  });
}

/**
 * Delete logo (stub - needs R2 integration)
 */
export function useDeleteLogo() {
  const queryClient = useQueryClient();
  const api = useApiClient();

  return useMutation({
    mutationFn: async ({ tenantId }: { tenantId: string }) => {
      await api.patch(`/tenants/${tenantId}`, {
        brandingConfig: { logo_url: null },
      });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['tenant', variables.tenantId] });
    },
  });
}

/**
 * Add custom color
 */
export function useAddCustomColor() {
  const queryClient = useQueryClient();
  const api = useApiClient();

  return useMutation({
    mutationFn: async ({
      tenantId,
      color,
      existingColors = [],
    }: {
      tenantId: string;
      color: SherwinWilliamsColor;
      existingColors?: SherwinWilliamsColor[];
    }) => {
      const newColors = [...existingColors, color];
      await api.patch(`/tenants/${tenantId}`, { customColors: newColors });
      return color;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['tenant', variables.tenantId] });
    },
  });
}

/**
 * Update custom color
 */
export function useUpdateCustomColor() {
  const queryClient = useQueryClient();
  const api = useApiClient();

  return useMutation({
    mutationFn: async ({
      tenantId,
      colorCode,
      code,
      updatedColor,
      color,
      existingColors = [],
    }: {
      tenantId: string;
      colorCode?: string;
      code?: string; // Alias for colorCode (backward compat)
      updatedColor?: SherwinWilliamsColor;
      color?: SherwinWilliamsColor; // Alias for updatedColor (backward compat)
      existingColors?: SherwinWilliamsColor[];
    }) => {
      const actualCode = colorCode || code;
      const actualColor = updatedColor || color;
      if (!actualCode || !actualColor) {
        throw new Error('colorCode/code and updatedColor/color are required');
      }
      const newColors = existingColors.map((c) =>
        c.code === actualCode ? actualColor : c
      );
      await api.patch(`/tenants/${tenantId}`, { customColors: newColors });
      return actualColor;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['tenant', variables.tenantId] });
    },
  });
}

/**
 * Delete custom color
 */
export function useDeleteCustomColor() {
  const queryClient = useQueryClient();
  const api = useApiClient();

  return useMutation({
    mutationFn: async ({
      tenantId,
      colorCode,
      code,
      existingColors = [],
    }: {
      tenantId: string;
      colorCode?: string;
      code?: string; // Alias for colorCode (backward compat)
      existingColors?: SherwinWilliamsColor[];
    }) => {
      const actualCode = colorCode || code;
      if (!actualCode) {
        throw new Error('colorCode or code is required');
      }
      const newColors = existingColors.filter((c) => c.code !== actualCode);
      await api.patch(`/tenants/${tenantId}`, { customColors: newColors });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['tenant', variables.tenantId] });
    },
  });
}

/**
 * Get available colors (combines standard SW colors with tenant custom colors)
 */
export function getAvailableColors(
  customColors: SherwinWilliamsColor[] = []
): SherwinWilliamsColor[] {
  // Combine standard colors with custom colors, prioritizing custom colors
  const customColorCodes = new Set(customColors.map((c) => c.code));
  const standardColors = SHERWIN_WILLIAMS_COLORS.filter(
    (c) => !customColorCodes.has(c.code)
  );
  return [...customColors, ...standardColors];
}
